const destinations = [
  {
    name: "Italy (Florence)",
    context: "FSU's Florence campus sends hundreds of students abroad each year. This is Tallahassee's most unique international travel pipeline — students need passports before program housing deposits, not just before flying",
    urgency: "FSU Florence acceptance letters arrive in Feb/March — passports must be ready well before June orientation",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "United Kingdom",
    context: "London and Oxford draw FSU law and business exchange students, state government policy delegates, and lobbying professionals attending international policy conferences",
    urgency: "UK Schengen-adjacent travel requires 3 months validity beyond departure — government delegates often book late",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Germany & Europe",
    context: "Berlin, Frankfurt, and Brussels attract Florida state trade mission participants and EU policy engagement from Enterprise Florida and the Governor's office. Technology and manufacturing delegations book 4–8 weeks out",
    urgency: "State trade missions often assemble delegate lists 6 weeks before departure — not enough time for standard processing",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Mexico & Latin America",
    context: "Cancun and Riviera Maya are popular FSU spring break destinations. Florida trade missions also target Mexico City, Bogota, Sao Paulo, and Buenos Aires as primary Latin American counterpart markets",
    urgency: "Spring break bookings from FSU spike in January — standard processing cannot meet March dates",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada",
    context: "Ottawa and Ottawa-Gatineau attract Florida government officials for intergovernmental policy exchange. Toronto draws legal and lobbying professionals for North American industry association meetings",
    urgency: "Government meetings in Canada often confirm with 2–3 week notice — passport validity rarely verified at booking",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Costa Rica & Caribbean",
    context: "Costa Rica is a popular FSU and TCC short-term program destination for environmental science, public health, and business students. Caribbean islands draw family vacation travelers during university break windows",
    urgency: "Short-term academic programs have fixed departure dates that cannot accommodate standard processing timelines",
    icon: "ri-map-pin-2-line",
  },
];

const stats = [
  { stat: "63%", label: "of Tallahassee expedited requests involve students, state government employees, or legal professionals" },
  { stat: "Feb–Mar", label: "peak study-abroad acceptance season that drives March–May passport urgency for FSU and FAMU" },
  { stat: "230+ Miles", label: "distance from Tallahassee to the Atlanta Passport Agency — the only public walk-in option" },
  { stat: "6 Weeks", label: "typical notice window for Florida state trade mission delegates to secure travel documents" },
];

export default function TallahasseeDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Tallahassee Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            From FSU Florence students to state government trade missions and legislative session travel, Tallahassee's travel patterns reflect its unique identity as a university and government capital city — not a beach or cruise market.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across the Florida Panhandle and Big Bend region with the same expedited passport expertise.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/gainesville" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Gainesville <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/panama-city" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Panama City <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/jacksonville" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Jacksonville <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}